import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Password input with real-time validation indicators.
 * Shows requirements as they're met (green check) or unmet (red x).
 *
 * @param {string} value - Current password value
 * @param {Function} onChange - Change handler
 * @param {string} [label] - Field label
 * @param {boolean} [required] - Show asterisk
 * @param {string} [className] - Additional classes for the wrapper
 * @param {boolean} [showRules=true] - Show inline validation rules
 */
export default function PasswordInput({ value, onChange, label, required, className, showRules = true }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);

  const rules = [
    { key: 'minLength', test: value.length >= 8, label: t('validation.passwordMin8') },
    { key: 'uppercase', test: /[A-Z]/.test(value), label: t('validation.passwordUppercase') },
    { key: 'lowercase', test: /[a-z]/.test(value), label: t('validation.passwordLowercase') },
    { key: 'digit', test: /[0-9]/.test(value), label: t('validation.passwordDigit') },
  ];

  const allValid = rules.every((r) => r.test);
  const showIndicators = showRules && touched && value.length > 0;

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label}{required && <span className="text-red-500 ms-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          onFocus={() => setTouched(true)}
          className={cn(
            'w-full rounded-lg border px-3 py-2 pe-10 text-sm focus:outline-none transition-colors',
            !touched || !value ? 'border-gray-300 focus:border-primary-500' :
            allValid ? 'border-emerald-300 focus:border-emerald-500' :
            'border-amber-300 focus:border-amber-500',
          )}
          dir="ltr"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute end-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>

      {showIndicators && (
        <div className="mt-2 space-y-1">
          {rules.map((rule) => (
            <div key={rule.key} className="flex items-center gap-1.5">
              {rule.test ? (
                <Check size={12} className="text-emerald-500 shrink-0" />
              ) : (
                <X size={12} className="text-red-400 shrink-0" />
              )}
              <span className={`text-[11px] ${rule.test ? 'text-emerald-600' : 'text-red-500'}`}>
                {rule.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Check if a password meets all requirements.
 * Use this in form validation before submit.
 */
export function isPasswordValid(password) {
  return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
}
